import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyStore, createOrUpdateStore } from '../store/storeSlice';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import { useToast } from '../../components/ui/Toast';
import './StoreInformation.css';

// ─── SVG Icon Components ────────────────────────────────────────────────────────

const StoreIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const PaletteIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r="0.5" fill={color} /><circle cx="17.5" cy="10.5" r="0.5" fill={color} />
    <circle cx="8.5" cy="7.5" r="0.5" fill={color} /><circle cx="6.5" cy="12" r="0.5" fill={color} />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

const ImageIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const SlidersIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

const FileTextIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const UploadIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const PlusIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ChevronLeftIcon = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const StarIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const SparklesIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813L20 10.5l-4.588 3.687L17.324 20 12 16.5 6.676 20l1.912-5.813L4 10.5l6.088-1.687z" />
  </svg>
);

const LoaderIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
);

// ─── Toggle Switch Component ────────────────────────────────────────────────────

const ToggleSwitch = ({ checked, onChange, label }) => (
  <div className="si-toggle-wrap">
    <div
      className={`si-toggle ${checked ? 'si-toggle--on' : 'si-toggle--off'}`}
      onClick={() => onChange(!checked)}
    >
      <div className="si-toggle-knob" />
    </div>
    {label && <span className="si-toggle-label">{label}</span>}
  </div>
);

// ─── Value Icon Map (SVG-based) ─────────────────────────────────────────────────

const VALUE_ICONS = {
  star: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  heart: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  ),
  check: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  lightbulb: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="9" y1="18" x2="15" y2="18" /><line x1="10" y1="22" x2="14" y2="22" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14" />
    </svg>
  ),
  shield: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  trophy: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
      <path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0012 0V2z" />
    </svg>
  ),
};

const VALUE_ICON_OPTIONS = [
  { value: 'star', label: 'Star' },
  { value: 'heart', label: 'Heart' },
  { value: 'check', label: 'Check' },
  { value: 'lightbulb', label: 'Lightbulb' },
  { value: 'shield', label: 'Shield' },
  { value: 'trophy', label: 'Trophy' },
];

// ─── Tab Definitions ────────────────────────────────────────────────────────────

const TABS = [
  { key: 'store', label: 'Store Information', Icon: StoreIcon },
  { key: 'about', label: 'About Page', Icon: FileTextIcon },
];

// ─── Main Component ─────────────────────────────────────────────────────────────

const StoreInformation = () => {
  const dispatch = useDispatch();
  const { getAxios } = useSecureAuth();
  const { current: store, status } = useSelector(s => s.store || {});
  const { success: toastSuccess, error: toastError } = useToast();

  // Fetch store data on mount
  useEffect(() => {
    if (!store && status !== 'loading') {
      const axios = getAxios();
      dispatch(fetchMyStore(axios));
    }
  }, [store, status, dispatch, getAxios]);

  // Update browser tab title with store name
  useEffect(() => {
    if (store?.name) {
      document.title = `${store.name} - Store Information`;
    }
  }, [store?.name]);

  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    contactPhone: '',
    businessAddress: '',
    zipCode: '',
    currency: 'NGN',
    logo: '',
    heroImage: '',
    heroTitle: '',
    heroDescription: '',
    heroSliderEnabled: false,
    heroSliderImages: [],
    heroOverlayOpacity: 0.4,
    aboutTitle: '',
    aboutDescription: '',
    aboutContent: '',
    aboutImage: '',
    aboutImagePosition: 'right',
    aboutCtaText: '',
    aboutCtaLink: 'shop',
    aboutHeroBackground: '',
    aboutValues: []
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingSlider, setUploadingSlider] = useState(false);
  const [uploadingAboutImage, setUploadingAboutImage] = useState(false);
  const [uploadingAboutHeroBackground, setUploadingAboutHeroBackground] = useState(false);
  const [activeTab, setActiveTab] = useState('store'); // 'store' or 'about'

  useEffect(() => {
    if (store) {
      const settings = store.settings || {};
      setFormData({
        name: store.name || '',
        tagline: settings.tagline || store.tagline || '',
        description: store.description || '',
        contactPhone: settings.contactPhone || '',
        businessAddress: settings.businessAddress || '',
        zipCode: settings.zipCode || '',
        currency: settings.currency || 'NGN',
        logo: store.logo || '',
        heroImage: store.heroImage || '',
        heroTitle: store.heroTitle || '',
        heroDescription: store.heroDescription || '',
        heroSliderEnabled: store.heroSliderEnabled || false,
        heroSliderImages: Array.isArray(store.heroSliderImages) ? store.heroSliderImages : [],
        heroOverlayOpacity: settings.heroOverlayOpacity !== undefined ? settings.heroOverlayOpacity : 0.4,
        aboutTitle: settings.aboutTitle || '',
        aboutDescription: settings.aboutDescription || '',
        aboutContent: settings.aboutContent || '',
        aboutImage: settings.aboutImage || '',
        aboutImagePosition: settings.aboutImagePosition || 'right',
        aboutCtaText: settings.aboutCtaText || '',
        aboutCtaLink: settings.aboutCtaLink || 'shop',
        aboutHeroBackground: settings.aboutHeroBackground || '',
        aboutValues: Array.isArray(settings.aboutValues) ? settings.aboutValues : []
      });
    }
  }, [store]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingHero;
    setUploading(true);

    try {
      const axios = getAxios();
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await axios.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.url) {
        handleChange(type === 'logo' ? 'logo' : 'heroImage', response.data.url);
        // Auto-save after image upload
        await autoSave({ ...formData, [type === 'logo' ? 'logo' : 'heroImage']: response.data.url });
      }
    } catch (error) {
      console.error(`Failed to upload ${type}:`, error);
      toastError(`Failed to upload ${type}. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  const handleSliderImageUpload = async (file) => {
    if (!file) return;

    setUploadingSlider(true);

    try {
      const axios = getAxios();
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await axios.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.url) {
        const newImages = [...formData.heroSliderImages, response.data.url];
        setFormData(prev => ({
          ...prev,
          heroSliderImages: newImages
        }));
        // Auto-save after slider image upload
        await autoSave({ ...formData, heroSliderImages: newImages });
      }
    } catch (error) {
      console.error('Failed to upload slider image:', error);
      toastError('Failed to upload slider image. Please try again.');
    } finally {
      setUploadingSlider(false);
    }
  };

  const handleRemoveSliderImage = async (index) => {
    const newImages = formData.heroSliderImages.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      heroSliderImages: newImages
    }));
    // Auto-save after removing image
    await autoSave({ ...formData, heroSliderImages: newImages });
  };

  const handleReorderSliderImages = (fromIndex, toIndex) => {
    const newImages = [...formData.heroSliderImages];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setFormData(prev => ({
      ...prev,
      heroSliderImages: newImages
    }));
  };

  const handleSliderToggle = async (enabled) => {
    setFormData(prev => ({
      ...prev,
      heroSliderEnabled: enabled
    }));
    // Auto-save after toggle
    await autoSave({ ...formData, heroSliderEnabled: enabled });
  };

  const handleAboutImageUpload = async (file) => {
    if (!file) return;

    setUploadingAboutImage(true);

    try {
      const axios = getAxios();
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await axios.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.url) {
        handleChange('aboutImage', response.data.url);
        // Auto-save after image upload
        await autoSave({ ...formData, aboutImage: response.data.url });
      }
    } catch (error) {
      console.error('Failed to upload about image:', error);
      toastError('Failed to upload about image. Please try again.');
    } finally {
      setUploadingAboutImage(false);
    }
  };

  const handleAboutHeroBackgroundUpload = async (file) => {
    if (!file) return;

    setUploadingAboutHeroBackground(true);

    try {
      const axios = getAxios();
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await axios.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.url) {
        handleChange('aboutHeroBackground', response.data.url);
        // Auto-save after image upload
        await autoSave({ ...formData, aboutHeroBackground: response.data.url });
      }
    } catch (error) {
      console.error('Failed to upload hero background:', error);
      toastError('Failed to upload hero background. Please try again.');
    } finally {
      setUploadingAboutHeroBackground(false);
    }
  };

  const autoSave = async (data) => {
    // Preserve scroll position to prevent page from jumping
    const scrollY = window.scrollY;

    try {
      const axios = getAxios();
      const payload = {
        name: data.name,
        tagline: data.tagline,
        description: data.description,
        logo: data.logo,
        heroImage: data.heroImage,
        heroTitle: data.heroTitle,
        heroDescription: data.heroDescription,
        heroSliderEnabled: data.heroSliderEnabled,
        heroSliderImages: data.heroSliderImages,
        settings: {
          ...(store?.settings || {}),
          tagline: data.tagline,
          contactPhone: data.contactPhone,
          businessAddress: data.businessAddress,
          zipCode: data.zipCode,
          currency: data.currency,
          heroOverlayOpacity: data.heroOverlayOpacity,
          aboutTitle: data.aboutTitle,
          aboutDescription: data.aboutDescription,
          aboutContent: data.aboutContent,
          aboutImage: data.aboutImage,
          aboutImagePosition: data.aboutImagePosition,
          aboutCtaText: data.aboutCtaText,
          aboutCtaLink: data.aboutCtaLink,
          aboutHeroBackground: data.aboutHeroBackground,
          aboutValues: data.aboutValues
        }
      };

      console.log('Auto-saving store with payload:', payload);
      await dispatch(createOrUpdateStore({ payload, axiosInstance: axios }));
      await dispatch(fetchMyStore(axios));
      toastSuccess('Changes saved automatically');

      // Restore scroll position after React re-render
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const handleSave = async () => {
    // Preserve scroll position to prevent page from jumping
    const scrollY = window.scrollY;

    try {
      const axios = getAxios();
      const payload = {
        name: formData.name,
        tagline: formData.tagline,
        description: formData.description,
        logo: formData.logo,
        heroImage: formData.heroImage,
        heroTitle: formData.heroTitle,
        heroDescription: formData.heroDescription,
        heroSliderEnabled: formData.heroSliderEnabled,
        heroSliderImages: formData.heroSliderImages,
        settings: {
          ...(store?.settings || {}),
          tagline: formData.tagline,
          contactPhone: formData.contactPhone,
          businessAddress: formData.businessAddress,
          zipCode: formData.zipCode,
          currency: formData.currency,
          heroOverlayOpacity: formData.heroOverlayOpacity,
          aboutTitle: formData.aboutTitle,
          aboutDescription: formData.aboutDescription,
          aboutContent: formData.aboutContent,
          aboutImage: formData.aboutImage,
          aboutImagePosition: formData.aboutImagePosition,
          aboutCtaText: formData.aboutCtaText,
          aboutCtaLink: formData.aboutCtaLink,
          aboutHeroBackground: formData.aboutHeroBackground,
          aboutValues: formData.aboutValues
        }
      };

      console.log('Saving store with payload:', payload);
      await dispatch(createOrUpdateStore({ payload, axiosInstance: axios }));
      await dispatch(fetchMyStore(axios));

      toastSuccess('Store information updated successfully!');

      // Restore scroll position after React re-render
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    } catch (error) {
      console.error('Failed to save store information:', error);
      toastError('Failed to update. Please try again.');
    }
  };

  // ─── Loading State ──────────────────────────────────────────────────────────

  if (status === 'loading' && !store) {
    return (
      <Shell>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="si-spinner" />
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading store information...</p>
          </div>
        </div>
      </Shell>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Shell>
      <div className="si-container">
        {/* Page Header */}
        <div className="si-page-header">
          <h1>Store Information</h1>
          <p>Manage your store details, branding, and appearance</p>
        </div>

        {/* Pill Tab Navigation */}
        <div className="si-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`si-tab ${activeTab === tab.key ? 'si-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <tab.Icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ─── Store Information Tab ─── */}
        {activeTab === 'store' && (
          <>
            {/* Basic Information */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <StoreIcon size={20} color="#0d7463" /> Basic Information
              </h3>

              <div className="si-grid-2">
                <div className="si-field">
                  <label className="si-label">Store Name</label>
                  <input
                    type="text"
                    className="si-input"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={handleSave}
                    placeholder="Enter your store name"
                  />
                </div>

                <div className="si-field">
                  <label className="si-label">Tagline</label>
                  <input
                    type="text"
                    className="si-input"
                    value={formData.tagline}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                    onBlur={handleSave}
                    placeholder="A catchy tagline for your store"
                  />
                </div>

                <div className="si-field si-grid-full">
                  <label className="si-label">Description</label>
                  <textarea
                    className="si-textarea"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    onBlur={handleSave}
                    placeholder="Tell customers about your store..."
                    rows="3"
                  />
                </div>

                <div className="si-field">
                  <label className="si-label">Contact Phone</label>
                  <input
                    type="tel"
                    className="si-input"
                    value={formData.contactPhone}
                    onChange={(e) => handleChange('contactPhone', e.target.value)}
                    onBlur={handleSave}
                    placeholder="+234 xxx xxx xxxx"
                  />
                </div>

                <div className="si-field">
                  <label className="si-label">Currency</label>
                  <select
                    className="si-select"
                    value={formData.currency}
                    onChange={(e) => { handleChange('currency', e.target.value); handleSave(); }}
                  >
                    <option value="NGN">NGN</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                <div className="si-field si-grid-full">
                  <label className="si-label">Business Address</label>
                  <input
                    type="text"
                    className="si-input"
                    value={formData.businessAddress}
                    onChange={(e) => handleChange('businessAddress', e.target.value)}
                    onBlur={handleSave}
                    placeholder="123 Main Street, City"
                  />
                </div>
              </div>
            </div>

            {/* Store Logo */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <PaletteIcon size={20} color="#0d7463" /> Store Logo
              </h3>

              <div className="si-img-upload-row">
                {formData.logo && (
                  <img
                    src={formData.logo}
                    alt="Store Logo"
                    className="si-img-preview"
                    style={{ width: '100px', height: '100px' }}
                  />
                )}

                <div className="si-img-upload-content">
                  <label className="si-upload-btn" style={{
                    cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                    opacity: uploadingLogo ? 0.5 : 1
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files && handleImageUpload(e.target.files[0], 'logo')}
                      disabled={uploadingLogo}
                      style={{ display: 'none' }}
                    />
                    {uploadingLogo ? <LoaderIcon size={16} /> : <UploadIcon size={16} />}
                    {uploadingLogo ? 'Uploading...' : formData.logo ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  <span className="si-hint">Recommended: Square image, at least 200x200px</span>
                </div>
              </div>
            </div>

            {/* Hero Background Image */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <ImageIcon size={20} color="#0d7463" /> Hero Background Image
              </h3>

              <div className="si-img-upload-row">
                {formData.heroImage && (
                  <img
                    src={formData.heroImage}
                    alt="Hero Background"
                    className="si-img-preview"
                    style={{ width: '220px', height: '124px' }}
                  />
                )}

                <div className="si-img-upload-content">
                  <label className="si-upload-btn" style={{
                    cursor: uploadingHero ? 'not-allowed' : 'pointer',
                    opacity: uploadingHero ? 0.5 : 1
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files && handleImageUpload(e.target.files[0], 'hero')}
                      disabled={uploadingHero}
                      style={{ display: 'none' }}
                    />
                    {uploadingHero ? <LoaderIcon size={16} /> : <UploadIcon size={16} />}
                    {uploadingHero ? 'Uploading...' : formData.heroImage ? 'Change Hero Image' : 'Upload Hero Image'}
                  </label>
                  <span className="si-hint">Recommended: 1920x1080px or 16:9 ratio</span>

                  {/* Hero Title & Description */}
                  <div style={{ marginTop: '1.25rem' }}>
                    <div className="si-field">
                      <label className="si-label">Hero Title</label>
                      <input
                        type="text"
                        className="si-input"
                        value={formData.heroTitle}
                        onChange={(e) => handleChange('heroTitle', e.target.value)}
                        onBlur={handleSave}
                        placeholder="Welcome to Our Store"
                      />
                    </div>

                    <div className="si-field">
                      <label className="si-label">Hero Description</label>
                      <textarea
                        className="si-textarea"
                        value={formData.heroDescription}
                        onChange={(e) => handleChange('heroDescription', e.target.value)}
                        onBlur={handleSave}
                        placeholder="Discover amazing products..."
                        rows="2"
                      />
                    </div>

                    {/* Hero Overlay Opacity */}
                    <div className="si-field">
                      <label className="si-label">Background Overlay Opacity</label>
                      <div className="si-range-wrap">
                        <input
                          type="range"
                          className="si-range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={formData.heroOverlayOpacity}
                          onChange={(e) => {
                            const newOpacity = parseFloat(e.target.value);
                            handleChange('heroOverlayOpacity', newOpacity);
                          }}
                          onMouseUp={handleSave}
                          onTouchEnd={handleSave}
                        />
                        <div className="si-range-value">
                          {Math.round(formData.heroOverlayOpacity * 100)}%
                        </div>
                      </div>
                      <span className="si-hint">
                        Controls the darkness of the overlay on your hero image. Higher values make text more readable.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image Slider */}
            <div className="si-card">
              <div className="si-flex-between" style={{ marginBottom: formData.heroSliderEnabled ? '1.5rem' : 0 }}>
                <h3 className="si-section-heading si-section-heading--no-border" style={{ flex: 1 }}>
                  <SlidersIcon size={20} color="#0d7463" /> Hero Image Slider
                </h3>
                <ToggleSwitch
                  checked={formData.heroSliderEnabled}
                  onChange={handleSliderToggle}
                  label={formData.heroSliderEnabled ? 'Enabled' : 'Disabled'}
                />
              </div>

              {formData.heroSliderEnabled && (
                <div>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label className="si-upload-btn" style={{
                      cursor: (uploadingSlider || formData.heroSliderImages.length >= 5) ? 'not-allowed' : 'pointer',
                      opacity: (uploadingSlider || formData.heroSliderImages.length >= 5) ? 0.5 : 1
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files && handleSliderImageUpload(e.target.files[0])}
                        disabled={uploadingSlider || formData.heroSliderImages.length >= 5}
                        style={{ display: 'none' }}
                      />
                      {uploadingSlider ? <LoaderIcon size={16} /> : <PlusIcon size={16} />}
                      {uploadingSlider ? 'Uploading...' : 'Add Slider Image'}
                    </label>
                    <span className="si-hint">
                      Add 2-5 images for your slider. Images auto-rotate every 5 seconds.
                      {formData.heroSliderImages.length >= 5 && ' (Maximum reached)'}
                    </span>
                  </div>

                  {formData.heroSliderImages.length > 0 ? (
                    <div className="si-slider-grid">
                      {formData.heroSliderImages.map((imageUrl, index) => (
                        <div key={index} className="si-slider-item">
                          <img src={imageUrl} alt={`Slider ${index + 1}`} />
                          <div className="si-slider-item-bar">
                            <span className="si-slider-item-label">Image {index + 1}</span>
                            <div className="si-slider-item-actions">
                              {index > 0 && (
                                <button
                                  className="si-slider-mini-btn"
                                  onClick={() => handleReorderSliderImages(index, index - 1)}
                                  title="Move left"
                                >
                                  <ChevronLeftIcon />
                                </button>
                              )}
                              {index < formData.heroSliderImages.length - 1 && (
                                <button
                                  className="si-slider-mini-btn"
                                  onClick={() => handleReorderSliderImages(index, index + 1)}
                                  title="Move right"
                                >
                                  <ChevronRightIcon />
                                </button>
                              )}
                              <button
                                className="si-slider-remove-btn"
                                onClick={() => handleRemoveSliderImage(index)}
                                title="Remove"
                              >
                                X
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="si-empty">
                      <div className="si-empty-icon">
                        <ImageIcon size={40} color="#9ca3af" />
                      </div>
                      <p className="si-empty-title">No slider images yet</p>
                      <p className="si-empty-text">Click "Add Slider Image" above to get started</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── About Page Tab ─── */}
        {activeTab === 'about' && (
          <>
            {/* About Page Configuration */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <FileTextIcon size={20} color="#0d7463" /> About Page Content
              </h3>

              <div className="si-grid-2">
                <div className="si-field">
                  <label className="si-label">Section Title</label>
                  <input
                    type="text"
                    className="si-input"
                    placeholder="Our Story"
                    value={formData.aboutTitle}
                    onChange={(e) => handleChange('aboutTitle', e.target.value)}
                    onBlur={() => autoSave(formData)}
                  />
                  <span className="si-hint">Main heading for the about section</span>
                </div>

                <div className="si-field">
                  <label className="si-label">Hero Description</label>
                  <input
                    type="text"
                    className="si-input"
                    placeholder="Learn more about our story..."
                    value={formData.aboutDescription}
                    onChange={(e) => handleChange('aboutDescription', e.target.value)}
                    onBlur={() => autoSave(formData)}
                  />
                  <span className="si-hint">Short description shown at the top</span>
                </div>
              </div>

              <div className="si-field" style={{ marginTop: '1.25rem' }}>
                <label className="si-label">About Content</label>
                <textarea
                  className="si-textarea"
                  rows={6}
                  placeholder="Tell your story here..."
                  value={formData.aboutContent}
                  onChange={(e) => handleChange('aboutContent', e.target.value)}
                  onBlur={() => autoSave(formData)}
                  style={{ resize: 'vertical' }}
                />
                <span className="si-hint">Main content for your about section</span>
              </div>
            </div>

            {/* About Section Image */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <ImageIcon size={20} color="#0d7463" /> About Section Image
              </h3>

              <div style={{ marginBottom: '1.25rem' }}>
                <label className="si-upload-btn" style={{
                  cursor: uploadingAboutImage ? 'not-allowed' : 'pointer',
                  opacity: uploadingAboutImage ? 0.5 : 1
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files && handleAboutImageUpload(e.target.files[0])}
                    disabled={uploadingAboutImage}
                    style={{ display: 'none' }}
                  />
                  {uploadingAboutImage ? <LoaderIcon size={16} /> : <UploadIcon size={16} />}
                  {uploadingAboutImage ? 'Uploading...' : formData.aboutImage ? 'Change Image' : 'Upload Image'}
                </label>
                <span className="si-hint">
                  Recommended: 800x600px or similar aspect ratio. This image appears next to your story text.
                </span>
              </div>

              {formData.aboutImage && (
                <div className="si-img-card">
                  <img src={formData.aboutImage} alt="About" />
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button
                      className="si-remove-btn"
                      onClick={() => {
                        handleChange('aboutImage', '');
                        autoSave({ ...formData, aboutImage: '' });
                      }}
                    >
                      <TrashIcon size={14} /> Remove Image
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Layout & CTA */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <SlidersIcon size={20} color="#0d7463" /> Layout & Call to Action
              </h3>

              <div className="si-grid-2">
                <div className="si-field">
                  <label className="si-label">Image Position</label>
                  <select
                    className="si-select"
                    value={formData.aboutImagePosition}
                    onChange={(e) => {
                      handleChange('aboutImagePosition', e.target.value);
                      autoSave({ ...formData, aboutImagePosition: e.target.value });
                    }}
                  >
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                  <span className="si-hint">Position of image relative to text</span>
                </div>

                <div className="si-field">
                  <label className="si-label">CTA Button Text</label>
                  <input
                    type="text"
                    className="si-input"
                    placeholder="Shop Now"
                    value={formData.aboutCtaText}
                    onChange={(e) => handleChange('aboutCtaText', e.target.value)}
                    onBlur={() => autoSave(formData)}
                  />
                  <span className="si-hint">Call-to-action button text</span>
                </div>
              </div>

              <div className="si-field" style={{ marginTop: '1.25rem' }}>
                <label className="si-label">CTA Button Link</label>
                <input
                  type="text"
                  className="si-input"
                  placeholder="shop"
                  value={formData.aboutCtaLink}
                  onChange={(e) => handleChange('aboutCtaLink', e.target.value)}
                  onBlur={() => autoSave(formData)}
                />
                <span className="si-hint">Page to link to (e.g., "shop", "products", etc.)</span>
              </div>
            </div>

            {/* Hero Background Image */}
            <div className="si-card">
              <h3 className="si-section-heading">
                <PaletteIcon size={20} color="#0d7463" /> About Hero Background
              </h3>

              <div style={{ marginBottom: '1.25rem' }}>
                <label className="si-upload-btn" style={{
                  cursor: uploadingAboutHeroBackground ? 'not-allowed' : 'pointer',
                  opacity: uploadingAboutHeroBackground ? 0.5 : 1
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files && handleAboutHeroBackgroundUpload(e.target.files[0])}
                    disabled={uploadingAboutHeroBackground}
                    style={{ display: 'none' }}
                  />
                  {uploadingAboutHeroBackground ? <LoaderIcon size={16} /> : <UploadIcon size={16} />}
                  {uploadingAboutHeroBackground ? 'Uploading...' : formData.aboutHeroBackground ? 'Change Background' : 'Upload Background'}
                </label>
                <span className="si-hint">
                  Recommended: 1920x1080px or wider. This appears at the top of your About page with a dark overlay for text readability.
                </span>
              </div>

              {formData.aboutHeroBackground && (
                <div className="si-img-card">
                  <div className="si-overlay-preview">
                    <img src={formData.aboutHeroBackground} alt="About Hero Background" />
                    <div className="si-overlay-preview-label">Preview with Overlay</div>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button
                      className="si-remove-btn"
                      onClick={() => {
                        handleChange('aboutHeroBackground', '');
                        autoSave({ ...formData, aboutHeroBackground: '' });
                      }}
                    >
                      <TrashIcon size={14} /> Remove Background
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Our Values */}
            <div className="si-card">
              <div className="si-flex-between" style={{ marginBottom: '1.5rem' }}>
                <h3 className="si-section-heading si-section-heading--no-border" style={{ flex: 1 }}>
                  <SparklesIcon size={20} color="#0d7463" /> Our Values
                </h3>
                <button
                  className="si-add-btn"
                  onClick={() => {
                    const newValue = {
                      title: 'New Value',
                      description: 'Describe this value...',
                      icon: 'star'
                    };
                    const newValues = [...formData.aboutValues, newValue];
                    handleChange('aboutValues', newValues);
                    autoSave({ ...formData, aboutValues: newValues });
                  }}
                >
                  <PlusIcon size={16} /> Add Value
                </button>
              </div>

              {formData.aboutValues.length > 0 ? (
                <div className="si-values-grid">
                  {formData.aboutValues.map((value, index) => (
                    <div key={index} className="si-value-card">
                      <div className="si-value-card-bar" />

                      <div className="si-value-card-header">
                        <div className="si-value-badge">
                          {VALUE_ICONS[value.icon || 'star']}
                          <span>Value {index + 1}</span>
                        </div>
                        <button
                          className="si-remove-btn"
                          onClick={() => {
                            const newValues = formData.aboutValues.filter((_, i) => i !== index);
                            handleChange('aboutValues', newValues);
                            autoSave({ ...formData, aboutValues: newValues });
                          }}
                        >
                          <TrashIcon size={14} /> Remove
                        </button>
                      </div>

                      <div className="si-value-field">
                        <label>Icon</label>
                        <select
                          className="si-select"
                          value={value.icon || 'star'}
                          onChange={(e) => {
                            const newValues = [...formData.aboutValues];
                            newValues[index] = { ...newValues[index], icon: e.target.value };
                            handleChange('aboutValues', newValues);
                            autoSave({ ...formData, aboutValues: newValues });
                          }}
                        >
                          {VALUE_ICON_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="si-value-field">
                        <label>Title</label>
                        <input
                          type="text"
                          className="si-input"
                          placeholder="Value Title"
                          value={value.title || ''}
                          onChange={(e) => {
                            const newValues = [...formData.aboutValues];
                            newValues[index] = { ...newValues[index], title: e.target.value };
                            handleChange('aboutValues', newValues);
                          }}
                          onBlur={() => autoSave(formData)}
                        />
                      </div>

                      <div className="si-value-field">
                        <label>Description</label>
                        <textarea
                          className="si-textarea"
                          rows={3}
                          placeholder="Describe this value..."
                          value={value.description || ''}
                          onChange={(e) => {
                            const newValues = [...formData.aboutValues];
                            newValues[index] = { ...newValues[index], description: e.target.value };
                            handleChange('aboutValues', newValues);
                          }}
                          onBlur={() => autoSave(formData)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="si-empty">
                  <div className="si-empty-icon">
                    <StarIcon size={40} color="#9ca3af" />
                  </div>
                  <p className="si-empty-title">No values added yet</p>
                  <p className="si-empty-text">
                    Click "Add Value" above to create your first value card and showcase what makes your business special
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Shell>
  );
};

export default StoreInformation;
